import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import { Mail, ExternalLink } from "lucide-react";

function isExternal(url) {
  return url && /^https?:/i.test(url);
}
function isMail(url) {
  return url && /^mailto:/i.test(url);
}

export default function ContactPage() {
  const { current } = useChampionship() || {};
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!current?.id) return;
    api.get(`/championships/${current.id}/contact-groups`).then((r) => setGroups(r.data || []));
  }, [current?.id]);

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-20" data-testid="contact-page">
      <div className="border-b border-border pb-10 mb-12">
        <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
          {current?.year} — {current?.location}
        </div>
        <h1 className="font-display text-5xl md:text-7xl font-extrabold tracking-tighter mt-3">Contact</h1>
        <p className="mt-4 text-muted-foreground max-w-2xl">
          The people behind the World Othello Championship organisation.
        </p>
      </div>

      {groups.length === 0 ? (
        <div className="text-muted-foreground" data-testid="contact-empty">
          Organisation contact information will appear here soon.
        </div>
      ) : (
        <div className="space-y-14">
          {groups.map((g) => (
            <section key={g.id} data-testid={`contact-group-${g.id}`}>
              <div className="flex items-baseline justify-between border-b border-border pb-3 mb-6">
                <h2 className="font-display text-2xl md:text-3xl font-bold tracking-tight">{g.title}</h2>
                {g.description && (
                  <p className="text-sm text-muted-foreground text-right max-w-md">{g.description}</p>
                )}
              </div>
              {g.contacts && g.contacts.length > 0 ? (
                <div className="border border-border bg-white">
                  {g.contacts.map((c, i) => (
                    <div
                      key={`${g.id}-${c.name || "row"}-${c.position || ""}-${i}`}
                      className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 items-center px-5 py-4 border-b border-border last:border-b-0"
                      data-testid={`contact-row-${g.id}-${i}`}
                    >
                      <div className="md:col-span-4">
                        {c.profile_url ? (
                          <a href={c.profile_url} target="_blank" rel="noreferrer" className="font-semibold hover:text-primary inline-flex items-center gap-1">
                            {c.name} <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          <span className="font-semibold">{c.name}</span>
                        )}
                      </div>
                      <div className="md:col-span-6 text-sm text-muted-foreground">{c.position}</div>
                      <div className="md:col-span-2 md:text-right">
                        {c.contact_link && (
                          <a
                            href={c.contact_link}
                            target={isExternal(c.contact_link) ? "_blank" : undefined}
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-primary font-medium hover:underline"
                          >
                            {isMail(c.contact_link) ? <Mail className="h-3.5 w-3.5" /> : <ExternalLink className="h-3.5 w-3.5" />}
                            Contact
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No individual contacts listed.</div>
              )}
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
